import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const DTICalculator = () => {
  const content = article.find((item) => item.id === 10);
  const [income, setIncome] = useState("6000");
  const [otherIncome, setOtherIncome] = useState("500");
  const [housingPayment, setHousingPayment] = useState("1800");
  const [debts, setDebts] = useState([
    { id: 1, name: "Auto Loan", amount: "450", type: "installment", interestRate: "5.5" },
    { id: 2, name: "Student Loan", amount: "300", type: "installment", interestRate: "4.2" },
    { id: 3, name: "Credit Cards", amount: "250", type: "revolving", interestRate: "18.9" },
    { id: 4, name: "Personal Loan", amount: "200", type: "installment", interestRate: "12.5" },
  ]);
  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [optimizationGoal, setOptimizationGoal] = useState("mortgage"); // mortgage, auto, personal, excellent
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = income && debts.some(debt => debt.amount);
    if (hasValidData) {
      setTimeout(() => {
        calculateDTI();
      }, 100);
    }
  }, [income, otherIncome, housingPayment, debts, optimizationGoal]);

  const handleDebtChange = (id, field, value) => {
    const newDebts = debts.map(debt => 
      debt.id === id ? { ...debt, [field]: value } : debt
    );
    setDebts(newDebts);
  };

  const addDebt = () => {
    const newDebt = {
      id: Date.now(),
      name: `Debt ${debts.length + 1}`,
      amount: "100",
      type: "installment",
      interestRate: "10.0"
    };
    setDebts([...debts, newDebt]);
  };

  const removeDebt = (id) => {
    if (debts.length > 1) {
      setDebts(debts.filter(debt => debt.id !== id));
    }
  };

  const resetCalculator = () => {
    setIncome("6000");
    setOtherIncome("500");
    setHousingPayment("1800");
    setDebts([
      { id: 1, name: "Auto Loan", amount: "450", type: "installment", interestRate: "5.5" },
      { id: 2, name: "Student Loan", amount: "300", type: "installment", interestRate: "4.2" },
      { id: 3, name: "Credit Cards", amount: "250", type: "revolving", interestRate: "18.9" },
      { id: 4, name: "Personal Loan", amount: "200", type: "installment", interestRate: "12.5" },
    ]);
    setResults(null);
    setOptimizationGoal("mortgage");
  };

  const calculateDTI = () => {
    const totalIncome = parseFloat(income || 0) + parseFloat(otherIncome || 0);
    const housing = parseFloat(housingPayment || 0);
    const totalDebt = debts.reduce((sum, debt) => sum + parseFloat(debt.amount || 0), 0);
    const totalMonthlyObligations = housing + totalDebt;

    if (!totalIncome) {
      alert("Please enter your income.");
      return;
    }

    // Calculate different DTI ratios
    const frontEndDTI = (housing / totalIncome) * 100;
    const backEndDTI = (totalMonthlyObligations / totalIncome) * 100;
    const debtOnlyDTI = (totalDebt / totalIncome) * 100;

    // Determine classifications
    const frontEndClassification = getClassification(frontEndDTI, "front");
    const backEndClassification = getClassification(backEndDTI, "back");
    const debtOnlyClassification = getClassification(debtOnlyDTI, "debt");

    // Get target DTI based on optimization goal
    const targetDTI = getTargetDTI(optimizationGoal);

    // Calculate improvement needed
    const improvementNeeded = Math.max(0, backEndDTI - targetDTI);
    const monthlyReductionNeeded = (improvementNeeded / 100) * totalIncome;

    // Generate recommendations
    const recommendations = generateRecommendations(
      backEndDTI, 
      frontEndDTI, 
      debts, 
      monthlyReductionNeeded,
      optimizationGoal
    );

    // Calculate debt breakdown
    const debtBreakdown = debts.map(debt => ({
      name: debt.name,
      amount: parseFloat(debt.amount || 0),
      type: debt.type,
      percentage: ((parseFloat(debt.amount || 0) / totalDebt) * 100).toFixed(2)
    }));

    // Calculate scenario comparison
    calculateScenarios(totalIncome, housing, totalDebt, debts, targetDTI);

    setResults({
      totalIncome: totalIncome.toFixed(2),
      housingPayment: housing.toFixed(2),
      totalDebt: totalDebt.toFixed(2),
      totalMonthlyObligations: totalMonthlyObligations.toFixed(2),
      frontEndDTI: frontEndDTI.toFixed(2),
      backEndDTI: backEndDTI.toFixed(2),
      debtOnlyDTI: debtOnlyDTI.toFixed(2),
      frontEndClassification,
      backEndClassification,
      debtOnlyClassification,
      improvementNeeded: improvementNeeded.toFixed(2),
      monthlyReductionNeeded: monthlyReductionNeeded.toFixed(2),
      targetDTI,
      recommendations,
      debtBreakdown,
      optimizationGoal
    });
  };

  const getClassification = (dti, type) => {
    if (type === "front") {
      if (dti < 28) return { level: "Excellent", color: "success" };
      if (dti < 33) return { level: "Good", color: "info" };
      if (dti < 36) return { level: "Fair", color: "warning" };
      return { level: "High Risk", color: "danger" };
    } else {
      if (dti < 20) return { level: "Excellent", color: "success" };
      if (dti < 36) return { level: "Good", color: "info" };
      if (dti < 43) return { level: "Fair", color: "warning" };
      if (dti < 50) return { level: "Risky", color: "danger" };
      return { level: "Very High Risk", color: "danger" };
    }
  };

  const getTargetDTI = (goal) => {
    switch(goal) {
      case "mortgage": return 36;
      case "auto": return 15;
      case "personal": return 40;
      case "excellent": return 20;
      default: return 36;
    }
  };

  const generateRecommendations = (backEndDTI, frontEndDTI, debts, monthlyReductionNeeded, goal) => {
    const recommendations = [];
    
    if (backEndDTI > 36) {
      recommendations.push({
        type: "critical",
        text: `Reduce monthly debt payments by $${monthlyReductionNeeded.toFixed(2)} to reach target DTI of ${getTargetDTI(goal)}%`
      });
    }

    if (frontEndDTI > 28) {
      recommendations.push({
        type: "warning",
        text: "Consider more affordable housing options to improve front-end DTI"
      });
    }

    // High-interest debt recommendations
    const highInterestDebts = debts.filter(debt => parseFloat(debt.interestRate) > 10);
    if (highInterestDebts.length > 0) {
      recommendations.push({
        type: "warning",
        text: `Focus on paying down high-interest debts: ${highInterestDebts.map(d => d.name).join(', ')}`
      });
    }

    // Credit card debt recommendations
    const creditCardDebts = debts.filter(debt => debt.type === "revolving");
    if (creditCardDebts.length > 0) {
      const totalCreditCard = creditCardDebts.reduce((sum, debt) => sum + parseFloat(debt.amount || 0), 0);
      recommendations.push({
        type: "info",
        text: `Consider consolidating $${totalCreditCard.toFixed(2)} in credit card debt to lower interest rates`
      });
    }

    if (backEndDTI < 20) {
      recommendations.push({
        type: "success",
        text: "Your DTI is excellent! You're in a strong position for new credit applications"
      });
    }

    return recommendations;
  };

  const calculateScenarios = (totalIncome, housing, totalDebt, debts, targetDTI) => {
    const scenarios = [
      { name: "Current", multiplier: 1, description: "Your current situation" },
      { name: "Pay Down 25%", multiplier: 0.75, description: "Reduce all debts by 25%" },
      { name: "Pay Down 50%", multiplier: 0.5, description: "Reduce all debts by 50%" },
      { name: "Increase Income 10%", multiplier: 1, incomeMultiplier: 1.1, description: "10% income increase" },
      { name: "Debt Consolidation", multiplier: 0.8, description: "20% reduction via consolidation" }
    ];

    const scenarioResults = scenarios.map(scenario => {
      const scenarioDebt = totalDebt * scenario.multiplier;
      const scenarioIncome = totalIncome * (scenario.incomeMultiplier || 1);
      const scenarioObligations = housing + scenarioDebt;
      const scenarioDTI = (scenarioObligations / scenarioIncome) * 100;
      
      const improvement = parseFloat(results?.backEndDTI || 0) - scenarioDTI;
      const meetsTarget = scenarioDTI <= targetDTI;

      return {
        name: scenario.name,
        description: scenario.description,
        dti: scenarioDTI.toFixed(2),
        improvement: improvement.toFixed(2),
        meetsTarget,
        monthlyDebt: scenarioDebt.toFixed(2),
        monthlyObligations: scenarioObligations.toFixed(2)
      };
    });

    setScenarioComparison(scenarioResults);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const incomeVsDebtData = results ? {
    labels: ['Monthly Income', 'Housing Payment', 'Other Debts', 'Remaining Income'],
    datasets: [
      {
        label: 'Amount ($)',
        data: [
          parseFloat(results.totalIncome),
          parseFloat(results.housingPayment),
          parseFloat(results.totalDebt),
          parseFloat(results.totalIncome) - parseFloat(results.totalMonthlyObligations)
        ],
        backgroundColor: ['#38a169', '#F06201', '#A03B00', '#FF8C42'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const dtiComparisonData = results ? {
    labels: ['Front-End DTI', 'Back-End DTI', 'Debt-Only DTI', 'Target DTI'],
    datasets: [
      {
        label: 'DTI Ratio (%)',
        data: [
          parseFloat(results.frontEndDTI),
          parseFloat(results.backEndDTI),
          parseFloat(results.debtOnlyDTI),
          results.targetDTI
        ],
        backgroundColor: ['#FF8C42', '#F06201', '#A03B00', '#38a169'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const debtBreakdownData = results ? {
    labels: results.debtBreakdown.map(debt => debt.name),
    datasets: [
      {
        label: 'Monthly Payment ($)',
        data: results.debtBreakdown.map(debt => debt.amount),
        backgroundColor: ['#F06201', '#FF8C42', '#A03B00', '#FFB088', '#FFD1B8'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const scenarioComparisonData = scenarioComparison ? {
    labels: scenarioComparison.map(scenario => scenario.name),
    datasets: [
      {
        label: 'DTI Ratio (%)',
        data: scenarioComparison.map(scenario => parseFloat(scenario.dti)),
        backgroundColor: scenarioComparison.map(scenario => 
          scenario.meetsTarget ? '#38a169' : '#F06201'
        ),
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const totalMonthlyDebt = debts.reduce((sum, debt) => sum + (parseFloat(debt.amount) || 0), 0);
  const totalMonthlyObligations = parseFloat(housingPayment || 0) + totalMonthlyDebt;

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Debt-to-Income (DTI) Ratio Calculator</h1>
          <p>Calculate and optimize your debt-to-income ratio for better loan approval chances</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Monthly Income:</span>
              <strong>${(parseFloat(income || 0) + parseFloat(otherIncome || 0)).toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Monthly Obligations:</span>
              <strong>${totalMonthlyObligations.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Active Debts:</span>
              <strong>{debts.length}</strong>
            </div>
            {results && (
              <div className="summary-item">
                <span>Back-End DTI:</span>
                <strong className={
                  results.backEndClassification.color === "success" ? "savings" :
                  results.backEndClassification.color === "warning" ? "warning" : "warning"
                }>
                  {results.backEndDTI}%
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Income & Debt Details</h2>
              <div className="card-actions">
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Optimize your debt-to-income ratio for specific loan types and improve your approval chances
              </div>
            </div>

            {/* Optimization Settings */}
            <div className="optimization-settings">
              <h3>Optimization Goal</h3>
              <div className="input-group">
                <label>Target Loan Type</label>
                <select 
                  value={optimizationGoal} 
                  onChange={(e) => setOptimizationGoal(e.target.value)}
                >
                  <option value="mortgage">Mortgage Approval (&lt;36% DTI)</option>
                  <option value="auto">Auto Loan (&lt;15% DTI)</option>
                  <option value="personal">Personal Loan (&lt;40% DTI)</option>
                  <option value="excellent">Excellent Credit (&lt;20% DTI)</option>
                </select>
              </div>
            </div><br/>

            {/* Income Section */}
            <div className="income-section">
              <h3>Monthly Income</h3>
              <div className="input-grid">
                <div className="input-group">
                  <label>Gross Monthly Income ($)*</label>
                  <input
                    type="number"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    placeholder="6000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="input-group">
                  <label>Other Monthly Income ($)</label>
                  <input
                    type="number"
                    value={otherIncome}
                    onChange={(e) => setOtherIncome(e.target.value)}
                    placeholder="500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div><br/>

            {/* Housing Payment */}
            <div className="housing-section">
              <h3>Housing Payment</h3>
              <div className="input-group">
                <label>Monthly Housing Payment ($)*</label>
                <input
                  type="number"
                  value={housingPayment}
                  onChange={(e) => setHousingPayment(e.target.value)}
                  placeholder="1800"
                  min="0"
                  step="0.01"
                />
                <small>Include mortgage/rent, property taxes, insurance, HOA fees</small>
              </div>
            </div><br/>

            {/* Debts Section */}
            <div className="debts-section">
              <div className="section-header">
                <h3>Monthly Debt Payments</h3>
                <button className="btn-secondary" onClick={addDebt}>
                  <i className="fa-solid fa-plus"></i> Add Debt
                </button>
              </div>

              <div className="debts-container">
                {debts.map((debt, index) => (
                  <div key={debt.id} className="debt-input-group">
                    <div className="debt-header">
                      <h4>{debt.name}</h4>
                      {debts.length > 1 && (
                        <button 
                          className="btn-remove" 
                          onClick={() => removeDebt(debt.id)}
                          title="Remove debt"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="input-grid">
                      <div className="input-group">
                        <label>Debt Name</label>
                        <input
                          type="text"
                          value={debt.name}
                          onChange={(e) => handleDebtChange(debt.id, "name", e.target.value)}
                          placeholder="Debt Name"
                        />
                      </div>
                      <div className="input-group">
                        <label>Monthly Payment ($)*</label>
                        <input
                          type="number"
                          value={debt.amount}
                          onChange={(e) => handleDebtChange(debt.id, "amount", e.target.value)}
                          placeholder="250"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="input-group">
                        <label>Debt Type</label>
                        <select 
                          value={debt.type} 
                          onChange={(e) => handleDebtChange(debt.id, "type", e.target.value)}
                        >
                          <option value="installment">Installment Loan</option>
                          <option value="revolving">Revolving Credit</option>
                          <option value="mortgage">Mortgage</option>
                          <option value="student">Student Loan</option>
                          <option value="auto">Auto Loan</option>
                          <option value="personal">Personal Loan</option>
                        </select>
                      </div>
                      <div className="input-group">
                        <label>Interest Rate (%)</label>
                        <input
                          type="number"
                          value={debt.interestRate}
                          onChange={(e) => handleDebtChange(debt.id, "interestRate", e.target.value)}
                          placeholder="10.0"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="required-note">* Required fields</div>
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateDTI}>
              Calculate DTI Ratio
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>DTI Optimization Guide</h2>
            <div className="default-message">
              <p>Enter your income and debt details to calculate your debt-to-income ratio and get personalized optimization strategies.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> DTI Improvement Tips:</h3>
                <ul>
                  <li>Front-end DTI (housing only) should be below 28% for most lenders</li>
                  <li>Back-end DTI (all debts) should be below 36% for optimal mortgage approval</li>
                  <li>Pay down high-interest debts first for maximum impact</li>
                  <li>Consider debt consolidation to lower monthly payments</li>
                  <li>Increasing your income can improve DTI as effectively as reducing debt</li>
                  <li>Avoid taking on new debt while working to improve your DTI ratio</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>DTI Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Back-End DTI</h3>
                  <div className={`summary-value ${
                    results.backEndClassification.color === "success" ? "savings" :
                    results.backEndClassification.color === "warning" ? "warning" : "warning"
                  }`}>
                    {results.backEndDTI}%
                  </div>
                  <div className="summary-note">{results.backEndClassification.level}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-home"></i></div>
                  <h3>Front-End DTI</h3>
                  <div className={`summary-value ${
                    results.frontEndClassification.color === "success" ? "savings" :
                    results.frontEndClassification.color === "warning" ? "warning" : "warning"
                  }`}>
                    {results.frontEndDTI}%
                  </div>
                  <div className="summary-note">{results.frontEndClassification.level}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-bullseye"></i></div>
                  <h3>Target DTI</h3>
                  <div className="summary-value">{results.targetDTI}%</div>
                  <div className="summary-note">
                    {results.optimizationGoal === "mortgage" ? "Mortgage" :
                     results.optimizationGoal === "auto" ? "Auto Loan" :
                     results.optimizationGoal === "personal" ? "Personal Loan" : "Excellent Credit"}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <h3>Monthly Reduction Needed</h3>
                  <div className="summary-value">${results.monthlyReductionNeeded}</div>
                  <div className="summary-note">To reach target</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Income vs Obligations</h3>
                    {incomeVsDebtData && <Bar data={incomeVsDebtData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Financial Breakdown:</strong> See how your income compares to your monthly housing and debt obligations.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>DTI Ratio Comparison</h3>
                    {dtiComparisonData && <Bar data={dtiComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Ratio Analysis:</strong> Compare different DTI calculations against your target ratio.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Debt Breakdown</h3>
                    {debtBreakdownData && <Bar data={debtBreakdownData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Debt Analysis:</strong> Understand how each debt contributes to your total monthly obligations.</p>
                    </div>
                  </div>

                  {scenarioComparison && (
                    <div className="chart-card">
                      <h3>Improvement Scenarios</h3>
                      {scenarioComparisonData && <Bar data={scenarioComparisonData} options={chartOptions} />}
                      <div className="chart-description">
                        <p><strong>Scenario Analysis:</strong> Compare different strategies for improving your DTI ratio.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    <div className="result-card">
                      <h3>DTI Ratio Analysis</h3>
                      <div className="result-stats">
                        <div className="stat">
                          <label>Total Monthly Income</label>
                          <span className="stat-value">${results.totalIncome}</span>
                        </div>
                        <div className="stat">
                          <label>Housing Payment</label>
                          <span className="stat-value">${results.housingPayment}</span>
                        </div>
                        <div className="stat">
                          <label>Total Monthly Debt</label>
                          <span className="stat-value">${results.totalDebt}</span>
                        </div>
                        <div className="stat">
                          <label>Total Monthly Obligations</label>
                          <span className="stat-value">${results.totalMonthlyObligations}</span>
                        </div>
                        <div className="stat">
                          <label>Front-End DTI (Housing)</label>
                          <span className={`stat-value ${
                            results.frontEndClassification.color === "success" ? "savings" :
                            results.frontEndClassification.color === "warning" ? "warning" : "warning"
                          }`}>
                            {results.frontEndDTI}% - {results.frontEndClassification.level}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Back-End DTI (Total)</label>
                          <span className={`stat-value ${
                            results.backEndClassification.color === "success" ? "savings" :
                            results.backEndClassification.color === "warning" ? "warning" : "warning"
                          }`}>
                            {results.backEndDTI}% - {results.backEndClassification.level}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Debt-Only DTI</label>
                          <span className={`stat-value ${
                            results.debtOnlyClassification.color === "success" ? "savings" :
                            results.debtOnlyClassification.color === "warning" ? "warning" : "warning"
                          }`}>
                            {results.debtOnlyDTI}% - {results.debtOnlyClassification.level}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Target DTI</label>
                          <span className="stat-value">{results.targetDTI}%</span>
                        </div>
                        <div className="stat">
                          <label>Improvement Needed</label>
                          <span className="stat-value">{results.improvementNeeded}%</span>
                        </div>
                        <div className="stat">
                          <label>Monthly Reduction Needed</label>
                          <span className="stat-value">${results.monthlyReductionNeeded}</span>
                        </div>
                      </div>
                    </div>

                    {/* Debt Breakdown */}
                    <div className="result-card">
                      <h3>Debt Breakdown</h3>
                      <div className="breakdown-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Debt Name</th>
                              <th>Type</th>
                              <th>Monthly Payment</th>
                              <th>Interest Rate</th>
                              <th>% of Total Debt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.debtBreakdown.map((debt, index) => (
                              <tr key={index}>
                                <td>{debt.name}</td>
                                <td>{debts.find(d => d.name === debt.name)?.type || 'N/A'}</td>
                                <td>${debt.amount}</td>
                                <td>{debts.find(d => d.name === debt.name)?.interestRate || 'N/A'}%</td>
                                <td>{debt.percentage}%</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan="2"><strong>Total</strong></td>
                              <td><strong>${results.totalDebt}</strong></td>
                              <td></td>
                              <td><strong>100%</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Scenario Comparison */}
                  {scenarioComparison && (
                    <div className="scenario-comparison">
                      <h3>Improvement Scenarios</h3>
                      <div className="scenario-grid">
                        {scenarioComparison.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>DTI Ratio</label>
                                <span className={scenario.meetsTarget ? "savings" : ""}>
                                  {scenario.dti}%
                                </span>
                              </div>
                              <div className="stat">
                                <label>Improvement</label>
                                <span className="savings">
                                  {scenario.improvement}%
                                </span>
                              </div>
                              <div className="stat">
                                <label>Monthly Debt</label>
                                <span>${scenario.monthlyDebt}</span>
                              </div>
                              <div className="stat">
                                <label>Meets Target</label>
                                <span className={scenario.meetsTarget ? "savings" : "warning"}>
                                  {scenario.meetsTarget ? "Yes" : "No"}
                                </span>
                              </div>
                              <div className="stat">
                                <label>Strategy</label>
                                <span>{scenario.description}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Recommendations */}
                  <div className="default-results">
                    <h3>Action Plan</h3>
                    <div className="recommendations-list">
                      {results.recommendations.map((rec, i) => (
                        <div key={i} className={`default-tips ${rec.type}`}>
                          <i className={`fa-solid ${
                            rec.type === 'critical' ? 'fa-exclamation-triangle' :
                            rec.type === 'warning' ? 'fa-exclamation-circle' :
                            rec.type === 'success' ? 'fa-check-circle' : 'fa-info-circle'
                          }`}></i>
                          <span>{rec.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your income and debt details to calculate and optimize your debt-to-income ratio.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default DTICalculator;